import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, EmptyState, TrustBadge, ScoreRing } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Filter, ChevronRight, ShieldCheck, UserCheck, Flag, Loader2 } from "lucide-react";

export default function Funnel({ inWorkspace = false }) {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [signing, setSigning] = useState(false);
  const [showSign, setShowSign] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/jobs/${jobId}/funnel`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [jobId]);

  const signoff = async () => {
    setSigning(true);
    try {
      const { data: r } = await api.post(`/jobs/${jobId}/funnel/signoff`, { note });
      setData((d) => ({ ...d, signoff: r.signoff }));
      setShowSign(false);
      toast.success("Shortlist approved — logged to audit trail");
    } catch { toast.error("Could not sign off"); }
    finally { setSigning(false); }
  };

  if (loading) return <LoadingState label="Building the screening funnel…" />;
  if (!data) return null;
  const maxCount = Math.max(...data.bands.map((b) => b.count), 1);

  return (
    <div className={inWorkspace ? "space-y-6" : "p-6 md:p-8 max-w-5xl space-y-6"} data-testid="screening-funnel">
      {!inWorkspace && <button onClick={() => nav("/recruiter/jobs")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="funnel-back"><ArrowLeft className="h-4 w-4" /> Jobs</button>}
      {inWorkspace ? (
        <div>
          <h2 className="text-lg font-display font-semibold">Screening funnel</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Evidence-based shortlisting. An AI score never rejects anyone — a person signs off on the shortlist.</p>
        </div>
      ) : (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Screening funnel</div>
          <h1 className="text-3xl font-display font-bold tracking-tight">{data.job.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">Evidence-based shortlisting. An AI score never rejects anyone — a human signs off on the shortlist.</p>
        </div>
      )}

      {/* human sign-off gate */}
      {data.signoff ? (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 flex items-center gap-3" data-testid="signoff-done">
          <UserCheck className="h-5 w-5 text-emerald-400 shrink-0" />
          <div className="text-sm"><span className="text-emerald-200 font-medium">Shortlist approved by {data.signoff.reviewer}</span><div className="text-xs text-emerald-200/80">"{data.signoff.note}"</div></div>
        </div>
      ) : (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 space-y-3" data-testid="signoff-gate">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-3"><ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" /><div><div className="text-sm font-medium text-amber-200">Human sign-off required</div><p className="text-xs text-amber-200/80">Review the AI-assisted shortlist below and approve before any adverse action.</p></div></div>
            {!showSign && <Button size="sm" onClick={() => setShowSign(true)} data-testid="open-signoff">Review & approve</Button>}
          </div>
          {showSign && (
            <div className="space-y-2">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note — logged to the audit trail" data-testid="signoff-note" />
              <div className="flex gap-2"><Button size="sm" onClick={signoff} disabled={signing} data-testid="confirm-signoff">{signing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve shortlist"}</Button><Button size="sm" variant="ghost" onClick={() => setShowSign(false)}>Cancel</Button></div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        {data.bands.map((band, bi) => (
          <div key={band.key} className="flex-1 flex items-stretch gap-4">
            <div className="flex-1 rounded-xl border border-border bg-card p-4 space-y-3" data-testid={`band-${band.key}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold flex items-center gap-1.5"><Filter className="h-3.5 w-3.5 text-primary" /> {band.label}</span>
                <span className="text-2xl font-display font-bold">{band.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(band.count / maxCount) * 100}%` }} /></div>
              <div className="space-y-2 pt-1">
                {band.candidates.length === 0 && <p className="text-xs text-muted-foreground">No candidates in this band.</p>}
                {band.candidates.slice(0, 6).map((c) => (
                  <div key={c.id} className="rounded-lg bg-muted/40 p-2.5 space-y-1.5" data-testid={`funnel-cand-${c.id}`}>
                    <div className="flex items-center gap-2">
                      {c.avatar ? <img src={c.avatar} alt="" loading="lazy" decoding="async" className="h-6 w-6 rounded-full object-cover" /> : <div className="h-6 w-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-[10px] font-semibold">{c.name[0]}</div>}
                      <span className="text-xs font-medium flex-1 truncate">{c.name}</span>
                      {c.score != null && <ScoreRing score={c.score} size={30} label="" />}
                    </div>
                    {band.key !== "applied" && c.reason && <p className="text-[11px] text-muted-foreground line-clamp-2">{c.reason}</p>}
                    {c.flags > 0 && <TrustBadge type="flag" className="scale-90 origin-left"><Flag className="h-3 w-3" />{c.flags}</TrustBadge>}
                  </div>
                ))}
              </div>
            </div>
            {bi < data.bands.length - 1 && <div className="hidden lg:flex items-center"><ChevronRight className="h-5 w-5 text-muted-foreground" /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}
